import { CSSProperties, ReactNode } from 'react';
import { TippyView } from '../tippy/tippy.view';

// DEV-501: the popover always carries the untruncated value, so it must be able to wrap.
// word-break lets a long unbroken string (e.g. an underscore-joined filename) wrap inside
// maxWidth instead of overflowing past the background. Those rules now live on .tippy-help-text
// in ../tippy/styles.scss, because tippy.js owns the bubble element. Deliberately no white-space,
// text-overflow or height cap there - the popover is never truncated, and it is non-interactive so
// a scrollbar inside it would be unreachable.
//
// DEV-919: this used to be react-tiny-popover. Its PopoverInternal measured the anchor and the
// popover in a useLayoutEffect and called setPopoverState on the result - a nested synchronous
// update whose dependency array held a freshly allocated DOMRect each pass. On the wrapped
// 400px-wide-by-several-lines-tall geometry DEV-501 introduced, that measurement stopped
// converging and React threw "Maximum update depth exceeded" at 50 nested commits. Tippy positions
// through Popper outside React's render cycle, so showing and hiding schedule no React state update
// at all, and a non-interactive tippy popper gets pointer-events: none, so it cannot steal a
// mouseleave from the cell it is covering. Both failure paths are closed, by separate mechanisms.
const HELP_TEXT_MAX_WIDTH = 400;

// Matches .react-tiny-popover-container in apps/vivli-webapp/src/GlobalCSSOverrides.css, which is
// what this popover rendered at before. tippy.js writes zIndex inline on the [data-tippy-root]
// wrapper, so it is not reachable from styles.scss. Without this the tooltip would drop to
// TippyView's default 1000 and render under the 9000 modal overlay.
const HELP_TEXT_Z_INDEX = 10001;

interface HelpTextViewProps {
    value?: ReactNode;
    scrollable?: boolean;
    truncate?: boolean;
    children?: ReactNode;
}

export const HelpTextView = ({ value, scrollable = true, truncate = false, children }: HelpTextViewProps) => {
    // DEV-501: truncate is opt-in per column. The default branch keeps the wrapping behavior
    // every other GridCellLongText column relies on.
    //
    // minWidth is a deliberate guard, not the thing that makes truncation work. This div is a
    // grid item of the 1fr track in grid-cell-long-text.view.tsx - minmax(auto, 1fr) - whose
    // auto minimum would be the filename's min-content width and would stretch the track past
    // the column, leaving no ellipsis. The overflowX below already suppresses that
    // content-based minimum (CSS Grid 6.6 applies it only when overflow is visible), so the
    // explicit 0 just keeps the clamp if that overflow value is ever changed.
    const containerStyle: CSSProperties = {
        height: '100%',
        overflowX: 'hidden',
        overflowY: scrollable ? 'auto' : 'hidden',
        lineHeight: '18px',
        ...(truncate ? { whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0 } : { whiteSpace: 'normal', wordBreak: 'break-word' })
    };

    const anchor = <div style={containerStyle}>{children}</div>;

    // An empty cell gets no tooltip at all. @tippyjs/react always hands tippy a container element
    // as its content, so tippy never sees "empty" and would otherwise render a bare blue box on
    // hover. This replaces the old `value && setIsOpen(true)` guard, and it avoids constructing a
    // tippy instance for every empty cell in a several-hundred-row grid.
    if (!value) {
        return anchor;
    }

    return (
        <TippyView content={value} className="tippy-help-text" maxWidth={HELP_TEXT_MAX_WIDTH} zIndex={HELP_TEXT_Z_INDEX}>
            {anchor}
        </TippyView>
    );
};
